import { motion as Motion, useReducedMotion } from 'framer-motion';
import s from './FlawlessXPReward.module.css';

const STAR_SRC = '/assets/xp-star.webp';

/**
 * Flawless-write reward (BP-FLAWLESS-XP-REWARD-01). Reserved for completions
 * with zero mistakes — the parent gates this on `lastResult.correct`. Mounted
 * absolutely over the writing canvas while the flawless result shows; renders
 * nothing structural so it can never shift the panel.
 *
 * Self-orchestrating, framer-motion only (ADR-044). The parent animates the
 * character itself (beat 1: canvas scale-up + fade). This component owns the
 * two later beats so each owns the center in turn, nothing stacks:
 *   2. Star  — fades in while rising ~36px with a gentle scale-in, then fades.
 *   3. +XP   — pops in (fade + scale), holds, then fades out.
 *
 * Reduced motion: drop the scale/translate choreography — star and +XP simply
 * cross-fade in place so the reward stays legible without motion.
 */
export default function FlawlessXPReward({ xp }) {
  const reduce = useReducedMotion();

  return (
    <div className={s.reward} aria-hidden="true">
      <Motion.img
        src={STAR_SRC}
        alt=""
        className={s.star}
        initial={{ opacity: 0 }}
        animate={
          reduce
            ? { opacity: [0, 1, 0] }
            : { opacity: [0, 1, 0], y: [0, -28, -36], scale: [0.6, 1, 1] }
        }
        transition={{ duration: 0.55, delay: 0.15, times: [0, 0.5, 1], ease: 'easeOut' }}
      />

      <Motion.div
        className={s.xpText}
        initial={{ opacity: 0 }}
        animate={
          reduce
            ? { opacity: [0, 1, 1, 0] }
            : { opacity: [0, 1, 1, 0], scale: [0.5, 1, 1, 1.05] }
        }
        transition={{ duration: 0.5, delay: 0.55, times: [0, 0.25, 0.7, 1], ease: 'easeOut' }}
      >
        <span className={s.xpAmount}>+{xp}</span>
        <span className={s.xpLabel}>XP</span>
      </Motion.div>
    </div>
  );
}
